import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { addPendingObservation, setCache, getCache } from "../../lib/offline";
import {
  Chips, Section, Field, PhotoUploader, VoiceNote, TaskModal, bLabel,
  GROWTH_STATUS, VIGOR_LEVELS, UNIFORMITY, VIGOR_OBS, GWI, SEVERITIES, HEALTH_ISSUES,
  DISEASES, INSECTS, WILDLIFE, IRRIGATION_ISSUES, WEED_PRESSURE, FLOOR_ITEMS,
  CULTURAL_OPS, CULTURAL_STATUS, PHENOLOGY_STAGES, SEV_STYLE,
} from "./parts";
import { AlertTriangle, CheckCircle2, Plus, Trash2, Loader2, CloudOff, TrendingUp } from "lucide-react";

const EMPTY = {
  phenology: {}, growth: {}, vigor: {}, health: { issues: [] }, pests: [],
  irrigation: { issues: [] }, weeds: {}, cultural: [], followups: [],
  internal_comments: "", client_comments: "", client_include: true,
};

const PCT_STAGES = { Budbreak: "budbreak_pct", Bloom: "bloom_pct", "Fruit Set": "fruitset_pct", Veraison: "veraison_pct" };

export default function BlockScout({ visit, block, photos, existingObs, onSaved, onNext, hasNext, onLocalSaved }) {
  const navigate = useNavigate();
  const [obs, setObs] = useState(EMPTY);
  const [ctx, setCtx] = useState(null);
  const [blockPhotos, setBlockPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedOffline, setSavedOffline] = useState(false);
  const [taskDefaults, setTaskDefaults] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    setObs(existingObs ? { ...EMPTY, ...existingObs } : EMPTY);
    setBlockPhotos((photos || []).filter((p) => p.ranch_id === block.id));
    api.get(`/scouting/context/${block.id}?visit_id=${visit.id}`)
      .then(({ data }) => { setCtx(data); setCache(`scout_ctx:${block.id}`, data); })
      .catch(async () => setCtx((await getCache(`scout_ctx:${block.id}`)) || null));
  }, [block.id]); // eslint-disable-line

  const set = (key, val) => setObs((o) => ({ ...o, [key]: val }));
  const setSub = (key, sub, val) => setObs((o) => ({ ...o, [key]: { ...(o[key] || {}), [sub]: val } }));

  const openTask = (section, note) => setTaskDefaults({
    client_id: visit.client_id, client_name: visit.client_name,
    ranch_number: visit.ranch_number, vineyard_name: visit.vineyard_name,
    block_ids: [block.id], block_labels: [block.block || block.name],
    acres: block.acres, visit_id: visit.id,
    observation_section: section, observation_note: note,
    title: note || "", category: section === "Irrigation" ? "IRRIGATION REPAIR" : section === "Cultural" || section === "Weeds" ? "TRACTOR WORK" : "CREW WORK",
  });

  const save = async (goNext) => {
    setSaving(true);
    const body = { ...obs, done: true };
    try {
      await api.put(`/scouting/visits/${visit.id}/blocks/${block.id}`, body);
      onSaved?.();
      if (goNext) onNext?.();
    } catch (e) {
      const offline = !e.response || e.response.status === 503 || !navigator.onLine;
      if (offline) {
        await addPendingObservation({
          id: `${visit.id}::${block.id}`, visit_id: visit.id, ranch_id: block.id,
          block_label: block.block || block.name, payload: body, queued_at: new Date().toISOString(),
        });
        setSavedOffline(true);
        onLocalSaved?.(block.id, { ...body, id: `local-${block.id}`, visit_id: visit.id, ranch_id: block.id, block_label: block.block || block.name });
        if (goNext) onNext?.();
      } else throw e;
    } finally { setSaving(false); }
  };

  const applyDraft = () => {
    if (!draft) return;
    setObs((o) => {
      const n = { ...o };
      if (draft.shoot_length_in != null) n.growth = { ...n.growth, shoot_length_in: draft.shoot_length_in };
      if (draft.growth_status) n.growth = { ...n.growth, status: draft.growth_status };
      if (draft.phenology_stage) n.phenology = { ...n.phenology, stage: draft.phenology_stage };
      if (draft.vigor) n.vigor = { ...n.vigor, level: draft.vigor };
      if (draft.weed_pressure) n.weeds = { ...n.weeds, pressure: draft.weed_pressure, comment: [n.weeds?.comment, draft.weed_location].filter(Boolean).join(" · ") };
      (draft.irrigation_issues || []).forEach((i) => {
        n.irrigation = { ...n.irrigation, status: "Issue", issues: [...(n.irrigation?.issues || []), { type: i.type, location: i.location || "", comment: "", priority: "normal" }] };
      });
      (draft.pests || []).forEach((p) => {
        n.pests = [...(n.pests || []), { group: "pest", name: p.name, severity: p.severity || "Low", location: p.location || "", comment: "", follow_up: false, priority: "normal" }];
      });
      if (draft.comment) n.internal_comments = [n.internal_comments, draft.comment].filter(Boolean).join("\n");
      return n;
    });
    setDraft(null);
  };

  const g = obs.growth || {};
  const prevLen = ctx?.prev_shoot_length;
  const liveChange = g.shoot_length_in != null && prevLen != null ? (g.shoot_length_in - prevLen).toFixed(1) : null;

  const addIssue = (key, item) => setObs((o) => ({ ...o, [key]: { ...(o[key] || {}), issues: [...((o[key] || {}).issues || []), item] } }));
  const updIssue = (key, idx, upd) => setObs((o) => {
    const issues = [...((o[key] || {}).issues || [])];
    issues[idx] = { ...issues[idx], ...upd };
    return { ...o, [key]: { ...(o[key] || {}), issues } };
  });
  const delIssue = (key, idx) => setObs((o) => {
    const issues = ((o[key] || {}).issues || []).filter((_, i) => i !== idx);
    return { ...o, [key]: { ...(o[key] || {}), issues } };
  });

  const addPest = (group, name) => set("pests", [...(obs.pests || []), { group, name, severity: "Low", incidence: "", row: "", location: "", comment: "", follow_up: false, priority: "normal" }]);
  const updPest = (idx, upd) => set("pests", obs.pests.map((p, i) => (i === idx ? { ...p, ...upd } : p)));
  const delPest = (idx) => set("pests", obs.pests.filter((_, i) => i !== idx));
  const [recBusy, setRecBusy] = useState(null);
  const recommendSpray = async (p, idx) => {
    setRecBusy(idx);
    try {
      await api.post("/pest/spray-orders/recommend", {
        ranch_number: visit.ranch_number, block_ids: [block.id], pest: p.name,
        severity: p.severity || null, visit_id: visit.id, note: p.comment || null,
      });
      updPest(idx, { spray_recommended: true });
    } catch (e) {
      window.alert("Could not queue the spray recommendation — this needs a connection.");
    } finally { setRecBusy(null); }
  };

  const culturalFor = (op) => (obs.cultural || []).find((c) => c.operation === op);
  const setCultural = (op, status, timing) => {
    const rest = (obs.cultural || []).filter((c) => c.operation !== op);
    if (!status || status === "NOT NEEDED") set("cultural", rest);
    else set("cultural", [...rest, { operation: op, status, timing: timing ?? culturalFor(op)?.timing ?? "" }]);
  };

  const setFollowup = (note, resolution) => {
    const rest = (obs.followups || []).filter((f) => f.ref_note !== note);
    set("followups", [...rest, { ref_note: note, resolution }]);
  };

  return (
    <div className="space-y-3 pb-40" data-testid="block-scout">
      {/* Header */}
      <div className="bg-[#1F3D34] text-white p-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
            {block.block ? bLabel(block.block) : block.name}
          </h2>
          <div className="flex items-center gap-3">
            <button type="button" data-testid="timeline-link" onClick={() => navigate(`/scouting/timeline/${block.id}`)}
              className="text-xs font-bold uppercase text-white/80 flex items-center gap-1 border border-white/30 px-2 py-1"><TrendingUp size={13} /> Timeline</button>
            <span className="text-sm text-white/70">{block.acres ? `${block.acres} ac` : ""}</span>
          </div>
        </div>
        <div className="text-sm text-white/80">{[block.variety, block.name !== block.block ? block.name : null].filter(Boolean).join(" · ")}</div>
        {ctx && (
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[["Last Scouted", ctx.last_scouted || "First visit"], ["Prev Phenology", ctx.prev_phenology || "—"],
              ["Prev Shoots", ctx.prev_shoot_length != null ? `${ctx.prev_shoot_length}"` : "—"], ["Prev Vigor", ctx.prev_vigor || "—"]].map(([k, v]) => (
              <div key={k} className="bg-white/10 p-2"><div className="text-white/50 uppercase text-[9px] font-bold">{k}</div><div className="font-semibold">{v}</div></div>
            ))}
          </div>
        )}
      </div>

      {/* Previous unresolved follow-ups */}
      {ctx?.unresolved?.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 p-4 space-y-3" data-testid="unresolved-panel">
          <div className="flex items-center gap-2 font-bold uppercase text-sm text-amber-900"><AlertTriangle size={16} /> Previous Observations — Follow-Up</div>
          {ctx.unresolved.map((u, i) => {
            const cur = (obs.followups || []).find((f) => f.ref_note === `${u.section}: ${u.note}`);
            return (
              <div key={`${u.section}:${u.note}`} className="bg-white border border-amber-200 p-3">
                <div className="text-sm font-semibold">{u.section}: {u.note}</div>
                {u.detail && <div className="text-xs text-stone-500">{u.detail}</div>}
                <div className="mt-2"><Chips small options={["Resolved", "Improved", "Unchanged", "Worse"]} value={cur?.resolution} onChange={(v) => setFollowup(`${u.section}: ${u.note}`, v)} /></div>
              </div>
            );
          })}
        </div>
      )}
      {ctx?.open_tasks?.length > 0 && (
        <div className="bg-white border border-stone-200 p-3 text-sm">
          <div className="font-bold uppercase text-xs mb-1">Open Tasks On This Block</div>
          {ctx.open_tasks.map((t) => (<div key={t.id} className="flex justify-between py-0.5"><span>{t.title}</span><span className="text-xs uppercase font-bold text-stone-500">{t.status}</span></div>))}
        </div>
      )}

      {/* Voice note */}
      <VoiceNote onDraft={(d, raw) => setDraft(d)} />
      {draft && (
        <div className="bg-[#7C9673]/10 border border-[#7C9673] p-4 space-y-2" data-testid="voice-draft-panel">
          <div className="font-bold uppercase text-sm text-[#1F3D34]">Draft Extracted — Review Before Applying</div>
          <div className="text-sm space-y-1">
            {draft.shoot_length_in != null && <div>• Shoot length: <b>{draft.shoot_length_in}"</b></div>}
            {draft.growth_status && <div>• Growth: <b>{draft.growth_status}</b></div>}
            {draft.phenology_stage && <div>• Phenology: <b>{draft.phenology_stage}</b></div>}
            {draft.vigor && <div>• Vigor: <b>{draft.vigor}</b></div>}
            {draft.weed_pressure && <div>• Weeds: <b>{draft.weed_pressure}</b>{draft.weed_location ? ` (${draft.weed_location})` : ""}</div>}
            {(draft.irrigation_issues || []).map((i, k) => <div key={k}>• Irrigation: <b>{i.type}</b>{i.location ? ` — ${i.location}` : ""}</div>)}
            {(draft.pests || []).map((p, k) => <div key={k}>• Pest: <b>{p.name}</b> {p.severity || ""}</div>)}
            {(draft.tasks || []).map((t, k) => (
              <div key={k} className="flex items-center gap-2">• Task: <b>{t.title}</b>{t.timing ? ` (${t.timing})` : ""}
                <button className="h-8 px-2 text-xs font-bold bg-[#1F3D34] text-white" onClick={() => openTask("Voice Note", t.title)}>Create Task</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="voice-apply-btn" onClick={applyDraft} className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-sm">Apply To Form</button>
            <button onClick={() => setDraft(null)} className="h-11 px-5 border border-stone-300 font-bold uppercase text-sm bg-white">Discard</button>
          </div>
        </div>
      )}

      {/* PHENOLOGY */}
      <Section title="Phenology" badge={obs.phenology?.stage} tone={obs.phenology?.stage ? "set" : ""} defaultOpen testId="section-phenology">
        <Chips options={PHENOLOGY_STAGES} value={obs.phenology?.stage} onChange={(v) => setSub("phenology", "stage", v)} testId="phenology-chips" />
        {PCT_STAGES[obs.phenology?.stage] && (
          <Field label={`${obs.phenology.stage} %`}>
            <div className="flex items-center gap-3">
              <input type="range" min={0} max={100} step={5} value={obs.phenology[PCT_STAGES[obs.phenology.stage]] ?? 0}
                onChange={(e) => setSub("phenology", PCT_STAGES[obs.phenology.stage], Number(e.target.value))} className="flex-1 accent-[#1F3D34]" />
              <span className="font-mono font-bold w-12 text-right">{obs.phenology[PCT_STAGES[obs.phenology.stage]] ?? 0}%</span>
            </div>
          </Field>
        )}
        <Field label="Comment"><input value={obs.phenology?.comment || ""} onChange={(e) => setSub("phenology", "comment", e.target.value)} className="w-full h-11 px-3 border border-stone-300" /></Field>
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Phenology" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* GROWTH */}
      <Section title="Vine Growth" badge={g.shoot_length_in != null ? `${g.shoot_length_in}"${liveChange != null ? ` (+${liveChange}")` : ""}` : g.status} tone={g.shoot_length_in != null || g.status ? "set" : ""} testId="section-growth">
        <div className="grid grid-cols-2 gap-3">
          <Field label='Avg Shoot Length (inches)'>
            <input type="number" inputMode="decimal" data-testid="shoot-length-input" value={g.shoot_length_in ?? ""}
              onChange={(e) => setSub("growth", "shoot_length_in", e.target.value === "" ? null : Number(e.target.value))}
              className="w-full h-12 px-3 border border-stone-300 font-mono text-lg" />
          </Field>
          {prevLen != null && (
            <div className="bg-stone-50 border border-stone-200 p-2 text-xs">
              <div className="font-bold uppercase text-[10px] text-stone-500">Since {ctx?.last_scouted}</div>
              <div className="font-mono">Prev {prevLen}"{liveChange != null && <> → <b>+{liveChange}"</b></>}</div>
              {liveChange != null && ctx?.last_scouted && (() => {
                const days = Math.max(1, Math.round((new Date(visit.visit_date) - new Date(ctx.last_scouted)) / 86400000));
                return <div className="font-mono">≈ {(liveChange / days).toFixed(1)}"/day over {days}d</div>;
              })()}
            </div>
          )}
        </div>
        <Field label="Growth Status"><Chips options={GROWTH_STATUS} value={g.status} onChange={(v) => setSub("growth", "status", v)} testId="growth-status-chips" /></Field>
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Growth" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* VIGOR */}
      <Section title="Vigor" badge={[obs.vigor?.level, obs.vigor?.uniformity].filter(Boolean).join(" · ")} tone={obs.vigor?.level ? "set" : ""} testId="section-vigor">
        <Chips options={VIGOR_LEVELS} value={obs.vigor?.level} onChange={(v) => setSub("vigor", "level", v)} testId="vigor-chips" />
        <Chips options={UNIFORMITY} value={obs.vigor?.uniformity} onChange={(v) => setSub("vigor", "uniformity", v)} />
        <Field label="Observations"><Chips small multi options={VIGOR_OBS} value={obs.vigor?.observations || []} onChange={(v) => setSub("vigor", "observations", v)} /></Field>
        <Field label="Comment"><input value={obs.vigor?.comment || ""} onChange={(e) => setSub("vigor", "comment", e.target.value)} className="w-full h-11 px-3 border border-stone-300" /></Field>
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Vigor" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* VINE HEALTH */}
      <Section title="Vine Health" badge={obs.health?.status || (obs.health?.issues?.length ? `${obs.health.issues.length} issues` : "")}
        tone={obs.health?.status === "Issue" ? "issue" : obs.health?.status === "Watch" ? "watch" : obs.health?.status ? "set" : ""} testId="section-health">
        <Chips options={GWI} value={obs.health?.status} onChange={(v) => setSub("health", "status", v)} testId="health-status-chips" />
        <Field label="Add Issue"><Chips small options={HEALTH_ISSUES} value={null} onChange={(v) => v && addIssue("health", { type: v, severity: "Low", location: "", comment: "", follow_up: false })} /></Field>
        {(obs.health?.issues || []).map((i, idx) => (
          <div key={idx} className="border border-stone-200 p-3 space-y-2 bg-stone-50">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">{i.type}</span>
              <button onClick={() => delIssue("health", idx)}><Trash2 size={15} className="text-stone-400" /></button>
            </div>
            <Chips small options={SEVERITIES} value={i.severity} onChange={(v) => updIssue("health", idx, { severity: v })} />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Location (rows, area…)" value={i.location || ""} onChange={(e) => updIssue("health", idx, { location: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
              <input placeholder="Comment" value={i.comment || ""} onChange={(e) => updIssue("health", idx, { comment: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => updIssue("health", idx, { follow_up: !i.follow_up })}
                className={`h-9 px-3 text-xs font-bold uppercase border ${i.follow_up ? "bg-amber-500 text-white border-amber-500" : "bg-white border-stone-300"}`}>Follow-Up</button>
              <button onClick={() => openTask("Vine Health", `${i.type} — ${i.severity || ""}`)} className="h-9 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white">Create Task</button>
            </div>
          </div>
        ))}
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Vine Health" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* PEST & DISEASE */}
      <Section title="Pest & Disease" badge={obs.pests?.length ? `${obs.pests.length} findings` : ""} tone={obs.pests?.length ? "issue" : ""} testId="section-pests">
        <div className="text-xs text-stone-500">Log exceptions and actual findings only.</div>
        <Field label="Diseases"><Chips small options={DISEASES} value={null} onChange={(v) => v && addPest("disease", v)} /></Field>
        <Field label="Insects"><Chips small options={INSECTS} value={null} onChange={(v) => v && addPest("insect", v)} /></Field>
        <Field label="Wildlife"><Chips small options={WILDLIFE} value={null} onChange={(v) => v && addPest("wildlife", v)} /></Field>
        {(obs.pests || []).map((p, idx) => (
          <div key={idx} className="border border-stone-200 p-3 space-y-2 bg-stone-50" data-testid={`pest-card-${idx}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">{p.name} <span className={`ml-1 text-[10px] px-1.5 py-0.5 uppercase font-bold ${SEV_STYLE[p.severity] || "bg-stone-200"}`}>{p.severity}</span></span>
              <button onClick={() => delPest(idx)}><Trash2 size={15} className="text-stone-400" /></button>
            </div>
            <Chips small options={SEVERITIES} value={p.severity} onChange={(v) => updPest(idx, { severity: v })} />
            <div className="grid grid-cols-3 gap-2">
              <input placeholder="Incidence / count" value={p.incidence || ""} onChange={(e) => updPest(idx, { incidence: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
              <input placeholder="Row / vine" value={p.row || ""} onChange={(e) => updPest(idx, { row: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
              <input placeholder="Location" value={p.location || ""} onChange={(e) => updPest(idx, { location: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
            </div>
            <input placeholder="Comment" value={p.comment || ""} onChange={(e) => updPest(idx, { comment: e.target.value })} className="w-full h-10 px-2 border border-stone-300 text-sm" />
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => updPest(idx, { follow_up: !p.follow_up })}
                className={`h-9 px-3 text-xs font-bold uppercase border ${p.follow_up ? "bg-amber-500 text-white border-amber-500" : "bg-white border-stone-300"}`}>Follow-Up</button>
              <Chips small options={["urgent", "high", "normal", "low"]} value={p.priority} onChange={(v) => updPest(idx, { priority: v || "normal" })} />
              <button onClick={() => openTask("Pest & Disease", `${p.name} — ${p.severity}`)} className="h-9 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white">Create Task</button>
              {p.group !== "wildlife" && (
                p.spray_recommended ? (
                  <span className="h-9 px-3 text-xs font-bold uppercase bg-[#7C9673]/20 text-[#1F3D34] flex items-center gap-1" data-testid={`spray-queued-${idx}`}>Spray Queued ✓</span>
                ) : (
                  <button onClick={() => recommendSpray(p, idx)} disabled={recBusy === idx}
                    className="h-9 px-3 text-xs font-bold uppercase bg-[#7C9673] text-white disabled:opacity-50"
                    data-testid={`recommend-spray-${idx}`}>
                    {recBusy === idx ? "Queuing…" : "Recommend Spray"}
                  </button>
                )
              )}
            </div>
          </div>
        ))}
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Pest & Disease" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* IRRIGATION */}
      <Section title="Irrigation" badge={obs.irrigation?.status || (obs.irrigation?.issues?.length ? `${obs.irrigation.issues.length} issues` : "")}
        tone={obs.irrigation?.status === "Issue" ? "issue" : obs.irrigation?.status === "Watch" ? "watch" : obs.irrigation?.status ? "set" : ""} testId="section-irrigation">
        <Chips options={GWI} value={obs.irrigation?.status} onChange={(v) => setSub("irrigation", "status", v)} testId="irrigation-status-chips" />
        <Field label="Add Issue"><Chips small options={IRRIGATION_ISSUES} value={null} onChange={(v) => v && addIssue("irrigation", { type: v, location: "", comment: "", priority: "normal" })} /></Field>
        {(obs.irrigation?.issues || []).map((i, idx) => (
          <div key={idx} className="border border-stone-200 p-3 space-y-2 bg-stone-50">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">{i.type}</span>
              <button onClick={() => delIssue("irrigation", idx)}><Trash2 size={15} className="text-stone-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Location (e.g. Row 18)" value={i.location || ""} onChange={(e) => updIssue("irrigation", idx, { location: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
              <input placeholder="Comment" value={i.comment || ""} onChange={(e) => updIssue("irrigation", idx, { comment: e.target.value })} className="h-10 px-2 border border-stone-300 text-sm" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Chips small options={["urgent", "high", "normal", "low"]} value={i.priority} onChange={(v) => updIssue("irrigation", idx, { priority: v || "normal" })} />
              <button onClick={() => openTask("Irrigation", `${i.type}${i.location ? ` — ${i.location}` : ""}`)} className="h-9 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white" data-testid="irrigation-create-task">Create Task</button>
            </div>
          </div>
        ))}
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Irrigation" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* WEEDS / FLOOR */}
      <Section title="Weeds / Floor" badge={obs.weeds?.pressure} tone={["High", "Severe"].includes(obs.weeds?.pressure) ? "issue" : obs.weeds?.pressure ? "set" : ""} testId="section-weeds">
        <Field label="Weed Pressure"><Chips options={WEED_PRESSURE} value={obs.weeds?.pressure} onChange={(v) => setSub("weeds", "pressure", v)} testId="weed-pressure-chips" /></Field>
        <Field label="Floor Management"><Chips small multi options={FLOOR_ITEMS} value={obs.weeds?.floor_items || []} onChange={(v) => setSub("weeds", "floor_items", v)} /></Field>
        <Field label="Comment"><input value={obs.weeds?.comment || ""} onChange={(e) => setSub("weeds", "comment", e.target.value)} className="w-full h-11 px-3 border border-stone-300" /></Field>
        <button onClick={() => openTask("Weeds", obs.weeds?.pressure ? `Weed pressure — ${obs.weeds.pressure}` : "Floor management")} className="h-9 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white">Create Task</button>
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="Weeds" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* CULTURAL WORK */}
      <Section title="Cultural Work" badge={(obs.cultural || []).length ? `${obs.cultural.length} flagged` : ""} tone={(obs.cultural || []).some((c) => ["READY", "NEEDS CORRECTION"].includes(c.status)) ? "watch" : (obs.cultural || []).length ? "set" : ""} testId="section-cultural">
        <div className="space-y-2">
          {CULTURAL_OPS.map((op) => {
            const c = culturalFor(op);
            return (
              <div key={op} className={`flex flex-wrap items-center gap-2 p-2 border ${c ? "border-[#7C9673] bg-[#7C9673]/5" : "border-stone-100"}`}>
                <span className="text-sm font-semibold w-44">{op}</span>
                <select value={c?.status || "NOT NEEDED"} onChange={(e) => setCultural(op, e.target.value)} className="h-10 px-2 border border-stone-300 text-xs font-bold">
                  {CULTURAL_STATUS.map((s) => <option key={s}>{s}</option>)}
                </select>
                {c && ["UPCOMING", "READY"].includes(c.status) && (
                  <input placeholder="Timing (e.g. within 3-5 days)" value={c.timing || ""} onChange={(e) => setCultural(op, c.status, e.target.value)} className="h-10 px-2 border border-stone-300 text-sm flex-1 min-w-[10rem]" />
                )}
                {c && ["READY", "NEEDS CORRECTION", "UPCOMING"].includes(c.status) && (
                  <button onClick={() => openTask("Cultural", `${op}${c.timing ? ` — ${c.timing}` : ""}`)} className="h-9 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white">Create Task</button>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* COMMENTS */}
      <Section title="Comments" badge={(obs.internal_comments || obs.client_comments) ? "✓" : ""} tone={(obs.internal_comments || obs.client_comments) ? "set" : ""} testId="section-comments">
        <Field label="Internal Comments (never shown to client)">
          <textarea data-testid="internal-comments" value={obs.internal_comments || ""} onChange={(e) => set("internal_comments", e.target.value)} rows={2} className="w-full border border-stone-300 p-3 text-sm bg-stone-50" />
        </Field>
        <Field label="Client Comments (eligible for client report)">
          <textarea data-testid="client-comments" value={obs.client_comments || ""} onChange={(e) => set("client_comments", e.target.value)} rows={2} className="w-full border border-[#7C9673] p-3 text-sm" />
        </Field>
        <PhotoUploader visitId={visit.id} ranchId={block.id} category="General" photos={blockPhotos} onChange={setBlockPhotos} />
      </Section>

      {/* Offline saved banner */}
      {savedOffline && (
        <div className="bg-amber-50 border border-amber-400 p-3 flex items-center gap-2 text-sm font-semibold text-amber-900" data-testid="offline-saved-banner">
          <CloudOff size={16} /> Saved offline — this block will sync automatically when signal returns.
        </div>
      )}

      {/* Sticky save bar */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-64 bg-[#F8F6F2]/95 backdrop-blur border-t border-stone-300 p-3 flex gap-2 z-30">
        <button data-testid="save-block-btn" onClick={() => save(false)} disabled={saving}
          className="flex-1 h-14 bg-white border-2 border-[#1F3D34] text-[#1F3D34] font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Save Block
        </button>
        {hasNext && (
          <button data-testid="save-next-btn" onClick={() => save(true)} disabled={saving}
            className="flex-1 h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
            Next Block <Plus size={18} />
          </button>
        )}
      </div>

      <TaskModal open={!!taskDefaults} onClose={() => setTaskDefaults(null)} defaults={taskDefaults || {}} onCreated={() => {}} />
    </div>
  );
}
